import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { api } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { formatDuration, KIND_KEY } from '../lib/contest';
import type { ParticipationSession, Question } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FullPageLoader } from '../components/ui/spinner';
import { Countdown } from '../components/controls/Countdown';

interface FinishSummary {
  alreadyFinished: boolean;
  summary: { score: number; correctCount: number; answeredCount: number; totalTimeMs: number };
}

/**
 * Synchronous participation.
 *
 * Everything that decides an outcome happens on the server: the start time, the
 * deadline, whether an answer is admissible, and the score. This page only sends
 * "question X = answer Y" and renders what comes back, so it cannot change a
 * score, extend a window, or answer twice.
 */
export default function ContestPlayPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();

  const [session, setSession] = useState<ParticipationSession | null>(null);
  const [fetchedAt, setFetchedAt] = useState(Date.now());
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [numeric, setNumeric] = useState('');
  const [answered, setAnswered] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finished, setFinished] = useState<FinishSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const start = useCallback(async () => {
    try {
      const data = await api.post<ParticipationSession>(`/contests/${id}/start`);
      setSession(data);
      setFetchedAt(Date.now());
      const answeredMap: Record<string, boolean> = {};
      for (const question of data.questions) if (question.answered) answeredMap[question.id] = true;
      setAnswered(answeredMap);
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void start();
  }, [start]);

  const current: Question | null = session?.questions[index] ?? null;

  // Reset the answer controls whenever the visible question changes.
  useEffect(() => {
    setSelected([]);
    setNumeric('');
    setNotice(null);
    setError(null);
  }, [index, current?.id]);

  const isMulti = current?.kind === 'multi_choice';
  const answeredCount = useMemo(() => Object.values(answered).filter(Boolean).length, [answered]);

  function toggleOption(optionId: string): void {
    if (!current) return;
    if (isMulti) {
      setSelected((previous) =>
        previous.includes(optionId)
          ? previous.filter((entry) => entry !== optionId)
          : [...previous, optionId],
      );
      return;
    }
    setSelected([optionId]);
  }

  async function submitAnswer(): Promise<void> {
    if (!current) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const body =
        current.kind === 'numeric'
          ? { questionId: current.id, numericValue: numeric }
          : { questionId: current.id, selectedOptionIds: selected };

      await api.post(
        `/contests/${id}/answers`,
        body,
        { headers: { 'Idempotency-Key': `${current.id}:${crypto.randomUUID()}` } },
      );

      setAnswered((previous) => ({ ...previous, [current.id]: true }));
      setNotice(t('contest.play.submitted'));
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setSubmitting(false);
    }
  }

  async function finish(): Promise<void> {
    setFinishing(true);
    setError(null);
    try {
      setFinished(await api.post<FinishSummary>(`/contests/${id}/finish`));
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setFinishing(false);
    }
  }

  if (loading) return <FullPageLoader label={t('common.loading')} />;

  if (finished) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <Card>
          <CardHeader>
            <CardTitle>{t('contest.play.finishedTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">
                  {t('contest.play.yourScore')}
                </dt>
                <dd className="text-2xl font-semibold text-fg">{finished.summary.score}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">
                  {t('contest.play.correctAnswers')}
                </dt>
                <dd className="text-2xl font-semibold text-fg">{finished.summary.correctCount}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">
                  {t('contest.play.answeredCount')}
                </dt>
                <dd className="text-fg">{finished.summary.answeredCount}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">
                  {t('contest.play.timeUsed')}
                </dt>
                <dd className="text-fg">{formatDuration(finished.summary.totalTimeMs)}</dd>
              </div>
            </dl>

            <Alert tone="info">{t('contest.play.meanwhile')}</Alert>

            <div className="flex flex-wrap gap-2">
              <Link to={`/contests/${id}`}>
                <Button variant="outline">{t('nav.contests')}</Button>
              </Link>
              <Link to="/my-entries">
                <Button variant="ghost">{t('nav.myEntries')}</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!session || !current) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <Alert tone="error">{error ?? t('contest.play.loadError')}</Alert>
        <div className="mt-6">
          <Link to={`/contests/${id}`}>
            <Button variant="outline">{t('nav.contests')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-fg">
            {t('contest.play.title')}
          </h1>
          <p className="text-sm text-muted">
            {t('contest.play.questionProgress', {
              current: index + 1,
              total: session.totalQuestions,
            })}
            {' · '}
            {t('contest.play.answered', { count: answeredCount })}
          </p>
        </div>
        <Countdown
          label={t('contest.play.timeLeft')}
          targetMs={Date.parse(session.endsAt)}
          serverTime={session.serverTime}
          fetchedAtMs={fetchedAt}
        />
      </header>

      {error && (
        <div className="mt-6">
          <Alert tone="error">{error}</Alert>
        </div>
      )}
      {notice && !error && (
        <div className="mt-6">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-sm font-normal text-muted">
              {t('contest.play.questionProgress', {
                current: index + 1,
                total: session.totalQuestions,
              })}
            </span>
            <span className="text-xs text-muted">
              {current.points} · {t(KIND_KEY[current.kind])}
            </span>
            {answered[current.id] && (
              <span className="ms-auto text-xs font-medium text-success-soft-fg">
                {t('contest.play.answered', { count: 1 })}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="whitespace-pre-line text-base text-fg">{current.prompt}</p>
          {current.hint && <p className="text-sm text-muted">{current.hint}</p>}

          <AnswerControls
            question={current}
            selected={selected}
            numeric={numeric}
            disabled={submitting || Boolean(answered[current.id])}
            onToggle={toggleOption}
            onNumeric={setNumeric}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={submitAnswer}
              loading={submitting}
              disabled={
                Boolean(answered[current.id]) ||
                (current.kind === 'numeric' ? numeric.trim().length === 0 : selected.length === 0)
              }
            >
              {submitting ? t('contest.play.submitting') : t('contest.play.submit')}
            </Button>

            <Button
              variant="outline"
              onClick={() => setIndex((value) => Math.max(0, value - 1))}
              disabled={index === 0}
            >
              {t('contest.play.previous')}
            </Button>

            {index < session.totalQuestions - 1 && (
              <Button variant="outline" onClick={() => setIndex((value) => value + 1)}>
                {t('contest.play.next')}
              </Button>
            )}

            <Button
              variant="secondary"
              className="ms-auto"
              onClick={finish}
              loading={finishing}
            >
              {finishing ? t('contest.play.finishing') : t('contest.play.finish')}
            </Button>
          </div>

          {answeredCount < session.totalQuestions && (
            <p className="text-xs text-muted">{t('contest.play.noAnswers')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** One answer control set, rendered per question kind. */
function AnswerControls({
  question,
  selected,
  numeric,
  disabled,
  onToggle,
  onNumeric,
}: {
  question: Question;
  selected: string[];
  numeric: string;
  disabled: boolean;
  onToggle: (optionId: string) => void;
  onNumeric: (value: string) => void;
}) {
  const { t } = useTranslation();

  if (question.kind === 'numeric') {
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={`answer-${question.id}`}>{t('contest.play.submit')}</Label>
        <Input
          id={`answer-${question.id}`}
          inputMode="decimal"
          value={numeric}
          disabled={disabled}
          placeholder={t('contest.play.numericPlaceholder')}
          onChange={(event) => onNumeric(event.target.value)}
        />
      </div>
    );
  }

  return (
    <fieldset className="flex flex-col gap-2" disabled={disabled}>
      <legend className="sr-only">{question.prompt}</legend>
      {question.kind === 'multi_choice' && (
        <p className="text-sm text-muted">{t('contest.play.multiHint')}</p>
      )}
      {question.options.map((option) => {
        const checked = selected.includes(option.id);
        return (
          <label
            key={option.id}
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-muted"
          >
            <input
              type={question.kind === 'multi_choice' ? 'checkbox' : 'radio'}
              name={`question-${question.id}`}
              checked={checked}
              disabled={disabled}
              onChange={() => onToggle(option.id)}
              className="h-4 w-4 accent-current text-brand"
            />
            <span className="text-fg">{option.label}</span>
          </label>
        );
      })}
    </fieldset>
  );
}

